import { Modal } from "antd";
import { useAppDispatch } from "core/utils";
import i18n from "i18next";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
    AppConfigsFieldEditableItem,
    AppConfigsFormJson,
    AppConfigsTab,
} from "src/app/features/ApplicationConfigurations/types/AppConfigsTypes";
import type { ImportError } from "src/app/features/ApplicationConfigurations/types/ImportError";
import { SecondaryButton } from "../../../../../components/Buttons/components/ActionButton";
import type { AntCustomFormField } from "../../../../../components/Form/types/types";
import { showErrorNotification, showWarningNotification } from "../../../../../components/NotificationAlerts";
import { normalizeConfigMap } from "../../../../../core/utils/ConvertConfigMap";
import { createMapFromList } from "../../../../../core/utils/CreateMapFromList";
import { mapFormModelToCollapseModel } from "../appConfigsUtils";

interface AppConfigImportButtonProps extends AntCustomFormField<AppConfigsFormJson> {}

export const AppConfigImportButton = (props: AppConfigImportButtonProps) => {
    const { value: currentState, onChange } = props;
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [currentImportFile, setCurrentImportFile] = useState("");

    const dispatch = useAppDispatch();
    const { t } = useTranslation();

    const requestUserImportFile = useCallback(() => {
        setIsModalVisible(true);
    }, [setIsModalVisible]);

    const onOk = useCallback(() => {
        if (currentImportFile) {
            if (!currentState) {
                return;
            }
            const importedData = JSON.parse(currentImportFile);
            if (!importedData) {
                dispatch(
                    showErrorNotification({
                        text: t("error.appConfig.import.badInput"),
                        autoCloseDisabled: true,
                    }),
                );
            }

            const errors: ImportError[] = [];

            const currentConfigMap = createMapFromList(currentState, "key");
            const importedConfigMap = createMapFromList(mapFormModelToCollapseModel(importedData, t), "key");

            const resourcesToUpdate = Object.keys(importedConfigMap);
            for (const resource of resourcesToUpdate) {
                if (Object.prototype.hasOwnProperty.call(currentConfigMap, resource)) {
                    // @ts-ignore
                    const importedTabsMap = createMapFromList(importedConfigMap[resource].at(0).configTabs, "key");
                    // @ts-ignore
                    const configTabsMap = createMapFromList(currentConfigMap[resource].at(0).configTabs, "key");

                    const tabsToUpdate = Object.keys(importedTabsMap);
                    for (const tab of tabsToUpdate) {
                        if (Object.prototype.hasOwnProperty.call(configTabsMap, tab)) {
                            const importedFieldsMap = createMapFromList(
                                (importedTabsMap[tab as keyof object].at(0) as AppConfigsTab).configFields!,
                                "name",
                            );
                            const configFieldsMap = createMapFromList(
                                (configTabsMap[tab as keyof object].at(0) as AppConfigsTab).configFields!,
                                "name",
                            );

                            const fieldsToUpdate: string[] = [];

                            for (const key of Object.keys(importedFieldsMap)) {
                                const fieldEntries = importedFieldsMap[key];
                                fieldEntries.forEach((field: AppConfigsFieldEditableItem) => {
                                    if (field.name != null) {
                                        fieldsToUpdate.push(field.name);
                                    }
                                });
                            }

                            for (const field of fieldsToUpdate) {
                                if (Object.prototype.hasOwnProperty.call(configFieldsMap, field)) {
                                    configFieldsMap[field] = importedFieldsMap[field];
                                } else {
                                    errors.push({ message: "error.appConfig.import.missingField", data: { field } });
                                }
                            }
                            // @ts-ignore
                            configTabsMap[tab].at(0).configFields = Object.values(configFieldsMap);
                        } else {
                            errors.push({ message: "error.appConfig.import.missingTab", data: { tab } });
                        }
                    }
                    // @ts-ignore
                    currentConfigMap[resource].at(0).configTabs = Object.values(configTabsMap);
                } else {
                    errors.push({ message: "error.appConfig.import.missingResource", data: { resource } });
                }
            }
            const processedData = normalizeConfigMap(currentConfigMap);

            if (errors) {
                const errorMsg = errors.map((err) => t(err.message, { ...err.data }));
                dispatch(
                    showWarningNotification({
                        text: errorMsg.join("\n"),
                        autoCloseDisabled: true,
                    }),
                );
            }

            if (processedData && onChange) {
                onChange(processedData);
            }

            setIsModalVisible(false);
        }
    }, [currentImportFile, currentState, t, dispatch, onChange]);

    const onCancel = useCallback(() => {
        setIsModalVisible(false);
    }, [setIsModalVisible]);

    const onSelectedFile = useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            const { files } = ev.target;
            const file = files?.item(0);
            if (file) {
                file.text().then((content) => setCurrentImportFile(content));
            }
        },
        [setCurrentImportFile],
    );

    return (
        <>
            <Modal open={isModalVisible} title={t("appConfigs.modal.import.title")} onOk={onOk} onCancel={onCancel}>
                <p>{t("appConfigs.modal.import.message")}</p>
                <input type="file" disabled={false} onChange={onSelectedFile} />
            </Modal>
            <SecondaryButton onClick={requestUserImportFile} disabled={false}>
                {i18n.t("button.import")}
            </SecondaryButton>
        </>
    );
};
